import type {OperationVariables} from '@apollo/client';
import {produce} from 'immer';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import type {ApolloError} from '../../../../../state/graphql/apolloWithAbort';
import {sliceArray} from '../../../../Pagination';

type PageInfo = {
  endCursor?: string | null;
  hasNextPage: boolean;
};

type PaginatedResult<TEdge> = {
  edges: readonly TEdge[];
  pageInfo?: PageInfo | null;
};

// Mutable version for use inside immer's produce
type MutablePaginatedResult<TEdge> = {
  edges: TEdge[];
  pageInfo?: PageInfo | null;
};

type UsePaginatedQueryConfig<TData, TEdge, TItem> = {
  getResult: (
    data: TData | undefined
  ) => PaginatedResult<TEdge> | null | undefined;
  parseEdges: (edges: readonly TEdge[]) => TItem[];
  getFetchMoreVariables: (endCursor: string) => Record<string, unknown>;
};

type QueryResultLike<TData, TVariables extends OperationVariables> = {
  data: TData | undefined;
  loading: boolean;
  error?: ApolloError | undefined;
  fetchMore: (options: {
    variables: Partial<TVariables>;
    updateQuery: (prev: TData, options: {fetchMoreResult: TData}) => TData;
  }) => Promise<unknown>;
};

type UsePaginatedQueryResult<TItem> = {
  items: TItem[];
  loading: boolean;
  error: ApolloError | undefined;
  totalLoaded: number;
  hasNextPage: boolean;
};

// Generic hook for paginated GraphQL queries with cursor-based pagination.
// Handles auto-fetching more data when needed and slicing for the current page.
export function usePaginatedQueryWithFilters<
  TData,
  TEdge,
  TItem,
  TVariables extends OperationVariables,
>(
  queryResult: QueryResultLike<TData, TVariables>,
  config: UsePaginatedQueryConfig<TData, TEdge, TItem>,
  page: number,
  pageSize: number
): UsePaginatedQueryResult<TItem> {
  const {data, loading, error, fetchMore} = queryResult;
  const {getResult, parseEdges, getFetchMoreVariables} = config;

  const [fetchMoreError, setFetchMoreError] = useState<ApolloError>();

  // Guard against concurrent fetchMore calls
  const isFetchingMoreRef = useRef(false);

  // Avoid effect re-runs from function identity changes; keep latest values
  const parseEdgesRef = useRef(parseEdges);
  // oxlint-disable-next-line react/refs
  parseEdgesRef.current = parseEdges;

  const getFetchMoreVariablesRef = useRef(getFetchMoreVariables);
  // oxlint-disable-next-line react/refs
  getFetchMoreVariablesRef.current = getFetchMoreVariables;

  const getResultRef = useRef(getResult);
  // oxlint-disable-next-line react/refs
  getResultRef.current = getResult;

  const {edges, pageInfo} = useMemo(() => {
    // oxlint-disable-next-line react/refs
    const result = getResultRef.current(data);
    return {
      edges: result?.edges ?? [],
      pageInfo: result?.pageInfo,
    };
  }, [data]);

  // Compute page items synchronously during render.
  // This avoids the flash of empty data that useState + useEffect can cause
  // when navigating between pages whose data is already loaded.
  const items = useMemo(() => {
    if (loading || error || fetchMoreError || edges.length === 0) {
      return [];
    }

    const sliced = sliceArray(edges as TEdge[], page, pageSize);

    if (sliced.length === 0 && edges.length > 0) {
      // Page is beyond available data — show last valid page as fallback
      const lastValidPage = Math.max(0, Math.ceil(edges.length / pageSize) - 1);
      // oxlint-disable-next-line react/refs
      return parseEdgesRef.current(
        sliceArray(edges as TEdge[], lastValidPage, pageSize)
      );
    }

    // oxlint-disable-next-line react/refs
    return parseEdgesRef.current(sliced);
  }, [edges, page, pageSize, loading, error, fetchMoreError]);

  const fetchMoreItems = useCallback(
    (endCursor: string) => {
      if (isFetchingMoreRef.current) {
        return;
      }
      isFetchingMoreRef.current = true;
      setFetchMoreError(undefined);

      fetchMore({
        variables: getFetchMoreVariablesRef.current(
          endCursor
        ) as Partial<TVariables>,
        updateQuery: (prev, {fetchMoreResult}) => {
          const newResult = getResultRef.current(fetchMoreResult);
          if (!newResult) {
            return prev;
          }

          return produce(prev, draft => {
            // Cast once at the boundary - immer's Draft type strips readonly
            const draftResult = getResultRef.current(draft as TData) as
              | MutablePaginatedResult<TEdge>
              | null
              | undefined;

            if (
              draftResult &&
              draftResult.pageInfo?.endCursor !== newResult.pageInfo?.endCursor
            ) {
              draftResult.pageInfo = newResult.pageInfo;
              draftResult.edges.push(...(newResult.edges as TEdge[]));
            }
          });
        },
      })
        .catch((err: ApolloError) => {
          setFetchMoreError(err);
        })
        .finally(() => {
          isFetchingMoreRef.current = false;
        });
    },
    [fetchMore]
  );

  // Trigger fetchMore when the current page needs more data than is loaded
  useEffect(() => {
    if (loading || error || fetchMoreError) {
      return;
    }

    if (edges.length < (page + 1) * pageSize && pageInfo?.hasNextPage) {
      fetchMoreItems(pageInfo?.endCursor ?? '');
    }
  }, [
    edges,
    pageInfo,
    page,
    pageSize,
    fetchMoreItems,
    loading,
    error,
    fetchMoreError,
  ]);

  return {
    items,
    loading,
    error: error ?? fetchMoreError,
    totalLoaded: edges.length,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
}
